import { Link } from 'react-router-dom'
import {Award, Users, ShieldCheck, Heart, CheckCircle2, Phone} from 'lucide-react'
import SectionHeader from '@/components/SectionHeader'
import FixNowBadge from '@/components/FixNowBadge'

const teamImg = 'https://static.lumi.new/24/24d7d0e5c85710147fff197ab9da948e.png'

const values = [
  { icon: Heart, title: 'Réactivité', desc: 'Chaque urgence est traitée immédiatement, sans délai inutile.' },
  { icon: ShieldCheck, title: 'Transparence', desc: 'Devis clair avant intervention. Aucune surprise sur la facture.' },
  { icon: Award, title: 'Qualité', desc: 'Plombiers certifiés, matériel professionnel, garantie sur tous nos travaux.' },
  { icon: Users, title: 'Proximité', desc: 'Une équipe locale, à votre écoute, dans toute la Suisse romande.' },
]

export default function AboutPage() {
  return (
    <div>
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-50 border border-urgent-100 text-urgent-700 text-xs font-bold uppercase tracking-wider">
              À propos
            </div>
            <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold text-navy-700 tracking-tight leading-[1.05] text-balance">
              Vous ne nous appelez pas pour de la plomberie. <span className="text-urgent-600">Vous nous appelez pour la tranquillité.</span>
            </h1>
            <p className="mt-5 text-lg text-navy-500 leading-relaxed">
              Depuis plus de 15 ans, nous accompagnons les particuliers, régies, syndics et commerces de Suisse romande pour résoudre leurs problèmes sanitaires, en urgence comme au quotidien.
            </p>
            <div className="mt-7 grid grid-cols-3 gap-4">
              <div>
                <div className="text-3xl lg:text-4xl font-extrabold text-urgent-500">15+</div>
                <div className="text-xs lg:text-sm text-navy-500 font-semibold mt-1">ans d'expérience</div>
              </div>
              <div>
                <div className="text-3xl lg:text-4xl font-extrabold text-urgent-500">5000+</div>
                <div className="text-xs lg:text-sm text-navy-500 font-semibold mt-1">interventions</div>
              </div>
              <div>
                <div className="text-3xl lg:text-4xl font-extrabold text-urgent-500">4.9/5</div>
                <div className="text-xs lg:text-sm text-navy-500 font-semibold mt-1">avis clients</div>
              </div>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-elevated border border-navy-100">
            <img src={teamImg} alt="Technicien NEXACO Sàrl de dos avec le logo sur le t-shirt" className="w-full h-[400px] lg:h-[520px] object-cover object-top" />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-navy-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Nos valeurs"
            title="Ce qui guide notre travail"
            subtitle="Plus que des plombiers : un partenaire de confiance pour vos installations sanitaires."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 border border-navy-100 shadow-soft">
                <div className="w-12 h-12 rounded-xl gradient-urgent flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-extrabold text-navy-700">{v.title}</h3>
                <p className="mt-1.5 text-sm text-navy-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Engagements"
            title="Nos certifications & garanties"
          />
          <div className="mt-10 bg-white rounded-3xl p-8 lg:p-12 border border-navy-100 shadow-soft">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Plombiers certifiés CFC",
                "Membre certifié FixNow - dépannage à domicile",
                "Assurance responsabilité civile professionnelle",
                "Membre suissetec / association professionnelle",
                "Garantie sur tous travaux et fournitures",
                "Respect des normes sanitaires suisses",
                "Tarification transparente et conforme",
                "Facturation claire avec TVA",
                "Confidentialité et respect du domicile",
              ].map((p) => (
                <div key={p} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-urgent-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-sm font-semibold text-navy-600">{p}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <FixNowBadge variant="light" />
          </div>

          <div className="mt-10 text-center">
            <a href="tel:+41796430399" className="inline-flex items-center gap-2 px-6 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Parler à un plombier
            </a>
            <div className="mt-3">
              <Link to="/contact" className="text-sm font-bold text-navy-600 hover:text-navy-700 underline">
                ou nous écrire
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
